/* Chrome = the display prefs the whole app reads: theme (dark default + light),
   density (comfortable/compact), and reduced-motion. Applied as data-* on <html>
   (so the DS token aliases re-derive) and persisted to localStorage. Ports the
   prototype's _applyChrome(). */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Theme = 'dark' | 'light';
export type Density = 'comfortable' | 'compact';

interface Chrome {
  theme: Theme;
  density: Density;
  motion: boolean;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
  setDensity: (d: Density) => void;
  setMotion: (on: boolean) => void;
}

const ChromeCtx = createContext<Chrome | null>(null);

const KEY = 'mc.chrome';
type Persisted = { theme: Theme; density: Density; motion: boolean };

/* iOS paints the status-bar / safe-area strip from <meta name="theme-color">.
   The two media-keyed metas in index.html only track the OS scheme — on a
   MANUAL toggle they go stale (the reported grey-strip bug). Fix: whenever the
   theme changes, rewrite every theme-color meta to the active page bg. Values
   mirror the DS tokens (--ct-bg): dark #0A0D14 · light #F6F8FC. */
const THEME_BG: Record<Theme, string> = { dark: '#0A0D14', light: '#F6F8FC' };

function syncThemeColor(theme: Theme): void {
  // Prefer the live token so the meta can never drift from the DS.
  const tokenBg = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
  const bg = tokenBg || THEME_BG[theme];
  const metas = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
  if (metas.length === 0) {
    const m = document.createElement('meta');
    m.name = 'theme-color';
    m.content = bg;
    document.head.appendChild(m);
    return;
  }
  // Rewrite ALL variants (incl. the media-keyed ones) so whichever the OS
  // matches, the painted strip follows the app theme — not the OS scheme.
  metas.forEach((m) => { m.content = bg; });
}

function load(): Persisted {
  const fallback: Persisted = { theme: 'dark', density: 'comfortable', motion: true };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return fallback;
    return { ...fallback, ...(JSON.parse(raw) as Partial<Persisted>) };
  } catch {
    return fallback;
  }
}

export function ChromeProvider({ children }: { children: ReactNode }) {
  const [{ theme, density, motion }, setState] = useState<Persisted>(load);

  // Reflect onto <html> so DS tokens re-derive and reduced-motion applies.
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('data-theme', theme);
    html.setAttribute('data-density', density);
    html.setAttribute('data-motion', motion ? 'on' : 'off');
    html.style.setProperty('--mc-gap', density === 'compact' ? '12px' : '16px');
    html.style.setProperty('--mc-pad', density === 'compact' ? '16px' : '22px');
    syncThemeColor(theme); // keep the iOS status-bar strip on the page bg
    try {
      localStorage.setItem(KEY, JSON.stringify({ theme, density, motion }));
    } catch {
      /* private mode — prefs just don't persist */
    }
  }, [theme, density, motion]);

  const value = useMemo<Chrome>(
    () => ({
      theme,
      density,
      motion,
      toggleTheme: () => setState((s) => ({ ...s, theme: s.theme === 'dark' ? 'light' : 'dark' })),
      setTheme: (t) => setState((s) => ({ ...s, theme: t })),
      setDensity: (d) => setState((s) => ({ ...s, density: d })),
      setMotion: (on) => setState((s) => ({ ...s, motion: on })),
    }),
    [theme, density, motion],
  );

  return <ChromeCtx.Provider value={value}>{children}</ChromeCtx.Provider>;
}

export function useChrome(): Chrome {
  const c = useContext(ChromeCtx);
  if (!c) throw new Error('useChrome must be used within <ChromeProvider>');
  return c;
}

/** Standalone hook for the initial <html> attributes before React mounts. */
export function primeChrome(): void {
  const { theme, density, motion } = load();
  const html = document.documentElement;
  html.setAttribute('data-theme', theme);
  html.setAttribute('data-density', density);
  html.setAttribute('data-motion', motion ? 'on' : 'off');
  syncThemeColor(theme); // persisted theme may disagree with the OS scheme
}
